import { observable, computed, action, reaction, toJS } from 'mobx';
import { app } from './../App';
import { debounce } from 'lodash';
import { user, search, warehouse } from './AllStores';
import * as moment from 'moment';
import * as accounting from 'accounting';
import {
    ISearchInputValues,
    ISearchItemInfo,
    ISearchItem,
    ISearchFitments,
    ICartInfo,
    ICartInfoItem,
    ICartItem,
    ShippingMethod,
    IOtherShippingType,
    IOtherShippingTypesApi,
    ICartItemsGroup
} from './../../../common/interfaces/search';
import { unformat } from 'accounting';
import Scroll from '../../../common/method/Scroll';
import { ICustomerAddress } from '../../../common/interfaces/user';
import Address from '../../../common/method/Address';
import { IWarehouse, IWarehouseStock } from '../../../common/interfaces/warehouse';
import { MakeModel } from '../../../common/helpers/makeModel';
import { IInsurance } from '../../../common/interfaces/insurance';

// Store
export class Cart {
    constructor() {
        this.getItemDebounced = debounce(this.getItemDebounced, 300);
        this.patchCartDebounced = debounce(this.patchCart, 1024);
    }

    public defaultCartName: string = 'Primary cart';

    private reactToCustomerChange = reaction(
        () => user.isCustomer,
        (isCustomer: boolean) => {
            if (isCustomer) {
                if (user.customerIsPBI) {
                    this.fetchOtherShippingTypes();
                }
                this.getCarts();
            } else {
                this.cartInfo = {
                    data: [],
                    limit: 0,
                    skip: 0,
                    total: 0,
                    hadMoreThanOneCart: false
                };
                this.showCartId = null;
                this.cartFetched = false;
                this.cartItemFetched = false;
                this.showCart = false;
                this.resetCartOptions();
            }
        }
    );

    @observable public showSaveCartNameField: boolean = false;
    @observable public saveCartName: string = '';
    @observable public savingCartForLater: boolean = false;
    @observable public saveCartNameIsRequired: boolean = false;

    @observable public showInsuranceJob: boolean = false;
    @observable public showShippingMethods: boolean = false;
    @observable public showShippingAddressesOrWarehouses: boolean = false;

    @observable public showOtherShippingTypes: boolean = false;
    @observable public otherShippingTypes: IOtherShippingType[] = [];

    private reactToShippingMethodChange = reaction(
        () => this.selectedShippingMethod,
        (method) => {
            if (method === 'pickup') {
                this.setOptimalWarehouse();
            }
        }
    );

    private reactToInCartUniqueItemsQuantityChange = reaction(
        () => this.currentCartTotalUniqueItems,
        (uniqueItemsCount) => {
            if (this.selectedShippingMethod === 'pickup' && uniqueItemsCount > 0) {
                this.setOptimalWarehouse();
            }
        }
    );

    private reactToCartTotalQuantityChange = reaction(
        () => this.currentCartTotalItemsQuantity,
        (totalItemsQuantity) => {
            if (this.selectedShippingMethod === 'pickup' && totalItemsQuantity > 0) {
                warehouse.recalculateWarehousesItemsAvailability();
            }
        }
    );

    @observable public selectedShippingMethod: ShippingMethod | null = null;
    @observable public selectedShippingAddressId: number | null = null;
    @observable public selectedPickupWarehouse: IWarehouseStock | null = null;
    @observable public selectedOtherShippingTypeId: number | null = null;
    @observable public selectedPoNumber: string = '';
    @observable public isInsuranceJob: boolean = false;
    @observable public selectedInsuranceJob: IInsurance | null = null;
    @observable public selectedOtherShippingTime: string = '';
    @observable public selectedOtherShippingAccountNumber: string = '';
    @observable public selectedOtherShippingInstructions: string = '';

    @computed
    public get selectedOtherShippingType(): IOtherShippingType | null {
        let shippingType: IOtherShippingType | null = null;

        if (this.selectedOtherShippingTypeId !== null && this.otherShippingTypes.length > 0) {
            this.otherShippingTypes.some((type) => {
                if (type.id === this.selectedOtherShippingTypeId) {
                    shippingType = type;
                    return true;
                }
            });
        }

        return shippingType;
    }

    @computed
    public get selectedShipmentMethodIsPickup(): boolean {
        return this.selectedShippingMethod === 'pickup';
    }

    @computed
    public get selectedShipmentMethodIncludesShipping(): boolean {
        return ['delivery', 'ltl', 'ltlwithliftgate', 'other'].indexOf(this.selectedShippingMethod) !== -1;
    }

    @computed
    public get selectedShippingAddressName(): string {
        if (this.selectedShippingAddressId !== null) {
            if (this.selectedShippingAddressId > 0) {
                let address: ICustomerAddress | null = null;
                user.customerAddresses.shipping.some((addr) => {
                    if (addr.addressId === this.selectedShippingAddressId) {
                        address = addr;
                        return true;
                    }
                });

                if (address === null) {
                    user.customerAddresses.billing.some((addr) => {
                        if (addr.addressId === this.selectedShippingAddressId) {
                            address = addr;
                            return true;
                        }
                    });
                }

                return address ? address.address : 'Address';
            } else {
                return 'Will-call pickup';
            }
        } else {
            return 'Address';
        }
    }

    @computed
    public get selectedShippingAddressDetails(): string | null {
        if (this.selectedShippingAddressId !== null && this.selectedShippingAddressId > 0) {
            let address: ICustomerAddress | null = null;
            user.customerAddresses.shipping.some((addr) => {
                if (addr.addressId === this.selectedShippingAddressId) {
                    address = addr;
                    return true;
                }
            });

            if (address === null) {
                user.customerAddresses.billing.some((addr) => {
                    if (addr.addressId === this.selectedShippingAddressId) {
                        address = addr;
                        return true;
                    }
                });
            }

            return address !== null ? Address.format(address) : null;
        } else {
            return null;
        }
    }

    @computed
    public get currentCartItemsAreNoLongerInStock(): boolean {
        return this.currentCart.item.some((item) => {
            const itemAvailableInStockCount = Object.keys(item.stock).reduce(
                (total, key) => {
                    return total + (item.stock[key].stock || 0);
                },
                0
            );

            return item.quantity > itemAvailableInStockCount;
        });
    }

    @observable
    public cartInfo: ICartInfo = {
        data: [],
        limit: 0,
        skip: 0,
        total: 0,
        hadMoreThanOneCart: false
    };
    @observable public showCartId: number = null;

    @computed
    private get allCartsIds(): number[] {
        return this.cartInfo.data.map((cart) => cart.id);
    }

    @observable public cartFetched: boolean = false;
    @observable public cartLoading: boolean = false;

    @observable public cartItemFetched: boolean = false;
    @observable public cartItemLoading: boolean = false;

    @observable public showCart: boolean = false;
    @observable public showCartsList: boolean = false;

    @computed
    public get currentCart(): ICartInfoItem | null {
        if (this.hasCart) {
            let cartFound = -1;
            this.cartInfo.data.some((cart, cartIndex) => {
                if (cart.id === this.showCartId) {
                    cartFound = cartIndex;
                    return true;
                }
            });

            return cartFound !== -1 ? this.cartInfo.data[cartFound] : null;
        } else {
            return null;
        }
    }

    @computed
    public get currentCartHasItems(): boolean {
        return (
            this.currentCart !== null &&
            typeof this.currentCart.item !== 'undefined' &&
            this.currentCart.item !== null &&
            typeof this.currentCart.item.length !== 'undefined' &&
            this.currentCart.item.length > 0
        );
    }

    @computed
    public get currentCartItemIds(): number[] {
        if (this.currentCartHasItems) {
            return this.currentCart.item.map((itemInCart) => itemInCart.id);
        } else {
            return [];
        }
    }

    @computed
    public get currentCartTotalItemsQuantity(): number {
        return this.currentCartHasItems
            ? this.currentCart.item.reduce((sum, item) => {
                  return sum + item.quantity;
              }, 0)
            : 0;
    }

    @computed
    public get currentCartTotalUniqueItems(): number {
        return this.currentCartHasItems ? this.currentCart.item.length : 0;
    }

    @computed
    public get currentCartHasItemsWithoutStock(): boolean {
        if (this.currentCartHasItems) {
            const someItemsHaveNoStock = this.currentCart.item.some((item) => {
                let stockCount: number = 0;
                stockCount += typeof item.stockCA !== 'undefined' && item.stockCA ? unformat(item.stockCA) : 0;
                stockCount += typeof item.stockTX !== 'undefined' && item.stockTX ? unformat(item.stockTX) : 0;
                stockCount += typeof item.stockVA !== 'undefined' && item.stockVA ? unformat(item.stockVA) : 0;
                stockCount += typeof item.LocalStock !== 'undefined' && item.LocalStock ? unformat(item.LocalStock) : 0;
                stockCount +=
                    typeof item.RegionalStock !== 'undefined' && item.RegionalStock ? unformat(item.RegionalStock) : 0;
                return stockCount === 0;
            });

            return someItemsHaveNoStock;
        }

        return false;
    }

    @computed
    public get currentCartTotalCost(): number {
        let price: number = 0;

        if (this.currentCartHasItems) {
            price = +Number(
                this.currentCart.item.reduce((sum, i) => {
                    const itemPrice =
                        i.price !== null && typeof i.price === 'string'
                            ? unformat(i.price)
                            : typeof i.price === 'number' && i.price > 0
                            ? i.price
                            : null;

                    const itemListPrice =
                        i.listPrice !== null && typeof i.listPrice === 'string'
                            ? unformat(i.listPrice)
                            : typeof i.listPrice === 'number'
                            ? i.listPrice
                            : 0;

                    return sum + (itemPrice !== null ? itemPrice * i.quantity : itemListPrice * i.quantity);
                }, 0)
            ).toFixed(2);
        }

        return price;
    }

    @computed
    public get currentCartItemsLength(): number {
        return this.currentCart.item.length;
    }

    @computed
    public get hasCart(): boolean {
        return (
            typeof this.cartInfo !== 'undefined' &&
            this.cartInfo !== null &&
            typeof this.cartInfo.data !== 'undefined' &&
            typeof this.cartInfo.data.length !== 'undefined' &&
            this.cartInfo.data.length > 0
        );
    }

    @computed
    public get hasMultipleCarts(): boolean {
        return this.hasCart && this.cartInfo.data.length > 1;
    }

    @computed
    public get currentCartItemsGroupedByCar(): ICartItemsGroup[] {
        const groups: ICartItemsGroup[] = [];

        if (this.currentCartHasItems) {
            this.currentCart.item.map((item) => {
                const concat = MakeModel.concatFromYearToYearMakeModel(item.fromYear, item.toYear, item.make, item.model);

                let groupExists: number = -1;
                groups.some((group, groupIndex) => {
                    if (group.concat === concat) {
                        groupExists = groupIndex;
                        return true;
                    }
                });

                if (groupExists !== -1) {
                    groups[groupExists].items.push(item);
                } else {
                    groups.push({
                        concat: concat,
                        fromYear: item.fromYear,
                        toYear: item.toYear,
                        make: item.make,
                        model: item.model,
                        items: [item]
                    });
                }
            });
        }

        return groups;
    }

    @action
    public showCartAction = (showCart: boolean = true, showCartLoading: boolean = true, refetch: boolean = false) => {
        if (showCart) {
            Scroll.disableBodyScroll();
        } else {
            Scroll.enableBodyScroll();
            this.showCartsList = false;
        }

        if (refetch && this.showCartId) {
            this.fetchCartItem(this.showCartId, false);
        }

        this.showSaveCartNameField = false;
        this.showCart = showCart;
    };

    @action
    public changeCartItemCount = async (item: ISearchItem, quantity: number, remove: boolean = false): Promise<ICartItem> => {
        try {
            let itemInCartFound = -1;
            if (this.currentCartHasItems) {
                this.currentCart.item.some((itemInCart, itemInCartIndex) => {
                    if (itemInCart.id === item.id) {
                        itemInCartFound = itemInCartIndex;
                        return true;
                    }
                });
            }

            if (itemInCartFound !== -1) {
                this.currentCart.item[itemInCartFound].quantity = quantity;

                // Re-show item if it's been removed before and is still in array, just hidden
                if (quantity > 0) {
                    this.currentCart.item[itemInCartFound].hidden = false;
                }

                // Remove item from cart completely if quantity is 0
                if (remove) {
                    this.currentCart.item[itemInCartFound].hidden = true;

                    // Hide cart if last item has been removed
                    let hiddenCount: number = 0;
                    this.currentCart.item
                        .filter((i) => i.hidden)
                        .map((item) => {
                            hiddenCount++;
                        });

                    if (hiddenCount >= this.currentCart.item.length) {
                        this.showCartAction(false);
                    }
                }
            } else {
                itemInCartFound = this.currentCart.item.length;
                this.currentCart.item.push({
                    ...item,
                    stock: {},
                    fitments: [],
                    showFitments: false,
                    quantity,
                    hidden: false
                });
            }

            this.patchCartDebounced();

            return this.currentCart.item[itemInCartFound];
        } catch (err) {
            console.error(err);
            return err;
        }
    };

    @action
    private fetchOtherShippingTypes = async (): Promise<IOtherShippingType[]> => {
        try {
            const service = app.service('shipping-method-other');
            const methods = (await service.find({ query: { $limit: 100, $skip: 0 } })) as IOtherShippingTypesApi;

            this.otherShippingTypes = methods.data;

            return methods.data;
        } catch (err) {
            console.error(err);
            return err;
        }
    };

    public getItemDebounced = () => {
        search.getItems();
    };

    @action
    public getCarts = async (showLoading: boolean = true, showCartOfId?: number): Promise<ICartInfo> => {
        try {
            if (showLoading) {
                this.cartLoading = true;
            }

            const service: any = app.service('cart');
            const carts = (await service.find({
                query: {
                    $limit: 999999,
                    $sort: {
                        updatedAt: -1
                    }
                }
            })) as ICartInfo;

            if (
                carts !== null &&
                typeof carts.total !== 'undefined' &&
                carts.total !== null &&
                this.cartInfo !== null &&
                typeof this.cartInfo.total !== 'undefined' &&
                this.cartInfo.total !== carts.total
            ) {
                this.cartInfo = {
                    total: carts.total,
                    limit: carts.limit,
                    skip: carts.skip,
                    data: carts.data.map((singleCart) => ({
                        ...singleCart,
                        total: singleCart.total > 0 ? singleCart.total : 0,
                        totalCount: singleCart.totalCount > 0 ? singleCart.totalCount : 0,
                        totalListPrice: singleCart.totalListPrice > 0 ? singleCart.totalListPrice : 0,
                        item: [],
                        deleteConfirmation: false
                    })),
                    hadMoreThanOneCart: carts.total > 1
                };
            }

            if (typeof showCartOfId !== 'undefined' && showCartOfId !== null) {
                this.showCartId = showCartOfId;
            }

            if (carts !== null && typeof carts.total !== 'undefined' && carts.total !== null && carts.total > 0) {
                this.setDefaultSelectedShippingAddress();
                this.setDefaultSelectedWarehouse();

                await this.fetchCartItem(this.showCartId ? this.showCartId : carts.data[0].id, showLoading);
            } else {
                setTimeout(async () => {
                    await this.getCarts();
                }, 500);
            }

            this.cartFetched = true;

            return this.cartInfo;
        } catch (err) {
            console.error(err);
            return err;
        } finally {
            this.cartLoading = false;
        }
    };

    @action
    public createCart = async (name: string, showCreatedCart: boolean = false): Promise<ICartInfoItem> => {
        try {
            const service: any = app.service('cart');
            const newCart = await service.create({ name });

            await this.getCarts(showCreatedCart, showCreatedCart ? newCart.id : null);

            return toJS(this.currentCart);
        } catch (err) {
            return err;
        } finally {
            this.cartLoading = false;
        }
    };

    @action
    public fetchCartItem = async (cartId: number, showLoading: boolean = true): Promise<ICartInfoItem> => {
        try {
            if (showLoading) {
                this.cartItemLoading = true;
            }

            this.showCartId = cartId;

            const service: any = app.service('cart');
            const items = (await service.get(cartId)) as ICartInfoItem;

            let foundCart = -1;
            this.cartInfo.data.some((cart, cartIndex) => {
                if (cart.id === cartId) {
                    foundCart = cartIndex;
                    return true;
                }
            });

            if (foundCart !== -1) {
                this.cartInfo.data[foundCart].total = items.total;
                this.cartInfo.data[foundCart].totalCount = items.totalCount;
                this.cartInfo.data[foundCart].totalListPrice = items.totalListPrice;
                this.cartInfo.data[foundCart].item = items.item;
            } else {
                this.cartInfo.data.push({
                    ...items
                });
            }

            return items;
        } catch (err) {
            console.error(err);
            return err;
        } finally {
            this.cartItemFetched = true;
            this.cartItemLoading = false;
        }
    };

    private patchCartDebounced = () => {
        this.patchCart();
    };

    @action
    public patchCart = async (): Promise<ICartInfoItem> => {
        try {
            const item = this.currentCart.item.map((v) => {
                let itemPatch: { itemId: number; quantity: number; delete?: boolean } = {
                    itemId: v.id,
                    quantity: v.quantity
                };

                if (v.quantity <= 0) {
                    itemPatch = {
                        ...itemPatch,
                        delete: true
                    };
                }

                return itemPatch;
            });

            const cartService = app.service('cart');
            const cartPatch = (await cartService.patch(this.currentCart.id, { item })) as ICartInfoItem;

            return this.currentCart;
        } catch (err) {
            console.error(err);
            return err;
        }
    };

    @action
    private setDefaultSelectedShippingAddress = async () => {
        if (this.selectedShippingAddressId === null) {
            this.selectedShippingAddressId =
                user.customerDefaultShippingAddress !== null
                    ? user.customerDefaultShippingAddress.addressId
                    : user.customerIsPBI
                    ? 0
                    : null;
        }
    };

    @action
    private setDefaultSelectedWarehouse = async (skipRecalculation: boolean = false) => {
        if (!skipRecalculation) {
            await warehouse.recalculateWarehousesItemsAvailability();
        }

        if (this.selectedPickupWarehouse === null) {
            let selectedWarehouse = {
                whsCode: user.customerDefaultWarehouseCode || 'PB',
                whsName: 'South El Monte',
                whsAllCartItemsAvailable: false
            };

            if (user.customerDefaultWarehouseCode !== null && warehouse.customerPickupWarehouses.length > 0) {
                warehouse.customerPickupWarehouses.some((whs) => {
                    if (whs.whsCode === selectedWarehouse.whsCode) {
                        selectedWarehouse = whs;
                        return true;
                    }
                });
            }

            this.selectedPickupWarehouse = selectedWarehouse;
        }
    };

    @action
    private setOptimalWarehouse = async () => {
        await warehouse.recalculateWarehousesItemsAvailability();

        if (this.selectedPickupWarehouse !== null && warehouse.customerPickupWarehouses.length > 0) {
            warehouse.customerPickupWarehouses.some((whs) => {
                if (whs.whsCode === this.selectedPickupWarehouse.whsCode) {
                    this.selectedPickupWarehouse = whs;
                    return true;
                }
            });
        } else {
            this.setDefaultSelectedWarehouse(true);
        }
    };

    @action
    public renameCurrentCart = async (name: string): Promise<any> => {
        try {
            const cartService = await app.service('cart');
            const cartNameUpdate = await cartService.patch(this.currentCart.id, { name });
            return cartNameUpdate;
        } catch (err) {
            console.error(err);
            return err;
        }
    };

    @action
    public saveCartForLater = async (): Promise<boolean> => {
        const errorNameIsRequired = 'Name is required';

        try {
            this.savingCartForLater = true;

            const nameFormatted: string = this.saveCartName
                .substr(0, 1)
                .toUpperCase()
                .concat(this.saveCartName.substr(1, this.saveCartName.length - 1))
                .trim();

            if (nameFormatted.length > 0) {
                const currentCartNameUpdate = await this.renameCurrentCart(nameFormatted);

                const createdCart = await this.createCart(moment().format(), true);
            } else {
                throw new Error(errorNameIsRequired);
            }

            return true;
        } catch (err) {
            this.saveCartNameIsRequired = true;
            console.error(err);
            return err;
        } finally {
            this.savingCartForLater = false;
        }
    };

    @action
    public switchToOtherCart = async (cartId: number): Promise<boolean> => {
        try {
            this.showCartsList = false;

            await this.fetchCartItem(cartId, true);

            if (typeof this.currentCart.id !== 'undefined' && this.currentCart.id !== null) {
                await this.renameCurrentCart(this.currentCart.name);
                this.currentCart.updatedAt = moment().format();
            }

            return true;
        } catch (err) {
            console.error(err);
            return err;
        }
    };

    @action
    public toggleCartDeleteConfirmation = (cartId: number, show?: boolean) => {
        const cartFound = this.allCartsIds.indexOf(cartId);

        if (cartFound !== -1) {
            this.cartInfo.data[cartFound].deleteConfirmation =
                typeof show !== 'undefined' ? show : !this.cartInfo.data[cartFound].deleteConfirmation;
        }
    };

    @action
    public deleteCart = async (cartId: number): Promise<boolean> => {
        try {
            this.toggleCartDeleteConfirmation(cartId, false);

            const cartFound = this.allCartsIds.indexOf(cartId);
            if (cartFound !== -1) {
                const isCurrent = this.cartInfo.data[cartFound].id === this.currentCart.id;

                this.cartInfo.data.splice(cartFound, 1);
                this.cartInfo.total--;

                const cartService = app.service('cart');
                const deleteCart = cartService.remove(cartId);

                if (isCurrent) {
                    await this.switchToOtherCart(this.cartInfo.data[0].id);
                }

                this.cartInfo.hadMoreThanOneCart = true;

                return true;
            } else {
                throw new Error('Cart not found');
            }
        } catch (err) {
            console.error(err);
            return err;
        }
    };

    @action
    public resetCartOptions = () => {
        this.showCartId = null;
        this.selectedShippingMethod = null;
        this.selectedShippingAddressId = null;
        this.selectedPickupWarehouse = null;
        this.selectedOtherShippingTypeId = null;
        this.selectedOtherShippingTime = '';
        this.selectedOtherShippingAccountNumber = '';
        this.selectedOtherShippingInstructions = '';
        this.isInsuranceJob = false;
        this.selectedInsuranceJob = null;
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/Cart.ts